import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Order from "@/pages/Order";
import Profile from "@/pages/Profile";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/components/ProtectedRoute";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TokenRedirect from "@/pages/TokenRedirect";
import { AuthProvider } from "@/contexts/AuthContext";
import { useEffect } from "react";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Special component to handle direct access token URLs - no UI, just redirects
const AccessTokenHandler = () => {
  useEffect(() => {
    // Execute redirect immediately
    handleTokenRedirect();
    
    // Also try again after a tiny delay in case the first attempt didn't work
    const timeoutId = setTimeout(() => {
      handleTokenRedirect();
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  const handleTokenRedirect = () => {
    // Get the current URL
    const currentUrl = window.location.href;
    const path = window.location.pathname;
    const hash = window.location.hash;
    const search = window.location.search;
    const origin = window.location.origin;
    
    // Log all URL parts for debugging
    console.log("AccessTokenHandler: Processing URL", currentUrl);
    console.log("AccessTokenHandler: Path", path);
    console.log("AccessTokenHandler: Hash", hash);
    console.log("AccessTokenHandler: Search", search);
    
    // Extract the token part - handle different possible formats
    let tokenParams = '';
    
    // Method 1: Check if token is in the path
    if (path.includes('access_token=')) {
      console.log("AccessTokenHandler: Found token in path");
      
      const tokenIndex = path.indexOf('access_token=');
      if (tokenIndex !== -1) {
        tokenParams = path.substring(tokenIndex);
        // Remove leading slash if present
        if (tokenParams.startsWith('/')) {
          tokenParams = tokenParams.substring(1);
        }
        console.log("AccessTokenHandler: Extracted token from path");
      }
    }
    
    // Method 2: Check hash fragment
    if (!tokenParams && hash.includes('access_token=')) {
      console.log("AccessTokenHandler: Found token in hash fragment");
      
      let hashContent = hash;
      // Remove leading # or #/
      if (hashContent.startsWith('#/')) {
        hashContent = hashContent.substring(2);
      } else if (hashContent.startsWith('#')) {
        hashContent = hashContent.substring(1);
      }
      
      // Check if there's a path before the query params
      const queryIndex = hashContent.indexOf('?');
      if (queryIndex !== -1) {
        hashContent = hashContent.substring(queryIndex + 1);
      } else if (hashContent.includes('access_token=')) {
        // The hash might directly contain the token without a ?
        tokenParams = hashContent;
      }
    }
    
    // Method 3: Use regex as a fallback
    if (!tokenParams) {
      console.log("AccessTokenHandler: Trying regex extraction");
      
      // Extract access token
      const tokenRegex = /access_token=([^&]+)/;
      const match = currentUrl.match(tokenRegex);
      
      if (match && match[1]) {
        const extractedToken = match[1];
        console.log("AccessTokenHandler: Extracted token using regex");
        
        // Construct the full token params
        tokenParams = 'access_token=' + extractedToken;
        
        // Try to extract other parameters
        const typeMatch = currentUrl.match(/type=([^&]+)/);
        if (typeMatch && typeMatch[1]) {
          tokenParams += '&type=' + typeMatch[1];
        }
        
        const refreshMatch = currentUrl.match(/refresh_token=([^&]+)/);
        if (refreshMatch && refreshMatch[1]) {
          tokenParams += '&refresh_token=' + refreshMatch[1];
        }
        
        const expiresInMatch = currentUrl.match(/expires_in=([^&]+)/);
        if (expiresInMatch && expiresInMatch[1]) {
          tokenParams += '&expires_in=' + expiresInMatch[1];
        }
        
        const expiresAtMatch = currentUrl.match(/expires_at=([^&]+)/);
        if (expiresAtMatch && expiresAtMatch[1]) {
          tokenParams += '&expires_at=' + expiresAtMatch[1];
        }
        
        const tokenTypeMatch = currentUrl.match(/token_type=([^&]+)/);
        if (tokenTypeMatch && tokenTypeMatch[1]) {
          tokenParams += '&token_type=' + tokenTypeMatch[1];
        }
        
        console.log("AccessTokenHandler: Final token params constructed");
      }
    }
    
    // Determine the redirect URL
    let redirectUrl;
    
    if (tokenParams) {
      // Make sure we have the full URL with origin
      redirectUrl = `${origin}/#/reset-password?${tokenParams}`;
      console.log("AccessTokenHandler: Redirect target set to reset password page");
    } else {
      // No token found, redirect to login
      redirectUrl = `${origin}/#/login`;
      console.log("AccessTokenHandler: No token found, redirecting to login");
    }
    
    // Execute multiple redirect methods simultaneously for better reliability
    try {
      console.log("AccessTokenHandler: Executing redirect now to", redirectUrl);
      
      // Method 1: location.replace (most reliable)
      window.location.replace(redirectUrl);
      
      // Method 2: location.href
      window.location.href = redirectUrl;
      
      // Method 3: Create and click a link
      const link = document.createElement('a');
      link.href = redirectUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Method 4: location.assign
      window.location.assign(redirectUrl);
    } catch (error) {
      console.error("AccessTokenHandler: Error during redirect:", error);
      // Final fallback
      window.location = redirectUrl as any;
    }
  };
  
  // Return null - no UI will be rendered
  return null;
};

function App() {
  // Check if the URL contains access_token directly in various formats
  const currentPath = window.location.pathname;
  const fullUrl = window.location.href;
  const hash = window.location.hash;
  
  // More comprehensive check for access tokens in the URL
  // Only trigger the handler if we're not already on the reset-password page
  if ((currentPath.includes('access_token=') || fullUrl.includes('access_token=')) && 
      !hash.includes('/#/reset-password') && 
      !hash.startsWith('#/reset-password')) {
    console.log("App: Detected direct access token URL");
    return <AccessTokenHandler />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Additional route to handle direct access with query parameters */}
            <Route path="reset-password" element={<ResetPassword />} />
            {/* Special catch-all route for access_token URLs */}
            <Route path="/access_token=*" element={<TokenRedirect />} />
            {/* Add a more general catch for paths containing access_token */}
            <Route path="*access_token=*" element={<TokenRedirect />} />
            <Route
              path="/order"
              element={
                <ProtectedRoute>
                  <Order />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
